/*Library Imports*/
import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowUpDown, ArrowUp, ArrowDown, MessageSquare } from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
/*Common Imports*/
import { ApiContext } from "../../../context/MyContext";
import Loader from "../../../common/Loader/Loader";
import RefreshButton from "../../../common/RefreshButton/RefreshButton";
import { Toaster } from "../../../common/Toaster/Toaster";
/*Service Imports*/
import { fetchAllUserDetails } from "../../../service/SubscriptionWellDhanService";
import { formatLocalDate } from "../../../utils/formatLocalDate";
import { expiryStatus } from "../../../utils/expiryStatus";
import {
  userListCache,
  myUserProfileCache,
  serializeUserListKey,
  USER_LIST_MAX_ROWS_PER_QUERY,
} from "../../../utils/listCache/caches";
import { useRoomsUnread } from "../../../hooks/comms/RoomsUnreadProvider";
import { selectClientRoom } from "../../../hooks/comms/useClientRoom";
import { useUtils } from "../../../context/UtilsContext";
import {
  prefetchUserProfile,
  resolveProfileEventTypeId,
} from "../../../utils/userProfileDigest";

/**
 * The only segment with chat/coaching-room awareness: these are the coach's
 * own clients, so they're the only ones worth caching locally, the only ones
 * with a coaching room, and the only rows that show the unread-chat badge and
 * "Texted first" reorder toggle.
 */
const SPLIT_VIEW_SEGMENT = "my_users";

/**
 * Expiry urgency → palette. Expired is the strongest signal; the 0-2 day
 * "critical" band is a lighter red so imminent reads as distinct from gone,
 * and 3-7 days is amber.
 */
const EXPIRY_CHIP = {
  expired: "bg-[#A32D2D] text-white",
  critical: "bg-[#FCEBEB] text-[#A32D2D]",
  soon: "bg-[#FAEEDA] text-[#8A5A00]",
  ok: "",
};

const EXPIRY_TEXT = {
  expired: "text-[#A32D2D]",
  critical: "text-[#A32D2D]",
  soon: "text-[#8A5A00]",
  ok: "",
};

const columnHelper = createColumnHelper();

/**
 * List segments (mutually exclusive). Recomputed each request for rolling windows.
 * my_users → only coach filter; others → column filters on aggregated fields.
 */
function buildListSegmentFilters(segment) {
  if (!segment || segment === "all") return null;
  if (segment === "my_users") return null;
  if (segment === "never_subscribed") {
    return { subscription_count: { max: 0 } };
  }
  if (segment === "lapsed") {
    return {
      is_currently_subscribed: { equals: false },
      subscription_count: { min: 1 },
    };
  }
  /** Not subscribed now, but had a plan on a past subscription and either pricing is still sold or the last plan window has ended (inactive period). */
  if (segment === "inactive_sub_plan_context") {
    return { inactive_sub_plan_context: { equals: true } };
  }
  if (segment === "currently_subscribed") {
    return { is_currently_subscribed: { equals: true } };
  }
  /** Any non-deleted subscription row has NULL payment_date. */
  if (segment === "missing_payment_date") {
    return { subscription_missing_payment_date: { equals: true } };
  }
  if (segment === "expiry_60d") {
    const start = new Date();
    const end = new Date(start);
    end.setDate(end.getDate() + 60);
    return {
      latest_plan_expiry_date: {
        min: start.toISOString(),
        max: end.toISOString(),
      },
    };
  }
  return null;
}

export const UserListDashboard = () => {
  const { setUserId } = useContext(ApiContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");
  const [totalCount, setTotalCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // Debounced search term
  const [sorting, setSorting] = useState([{ id: 'user_joining_date', desc: true }]);
  const [columnSizing, setColumnSizing] = useState({});
  /** One of the preset segment filters, or null = show all users (no segment filter). */
  const [listSegment, setListSegment] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [_offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const limit = 100;
  const loadingRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const offsetRef = useRef(0);
  const sentinelRef = useRef(null);
  const fetchDataRef = useRef(null);
  const hasMoreRef = useRef(hasMore);
  const usersRef = useRef([]);
  const queryKeyRef = useRef("");
  const revalidatingRef = useRef(false);

  const EVENT_TYPE_ID = "4850387a-8838-45d5-8c65-dce9d1f86de6";

  // Already-fetched room data (RoomsUnreadProvider lists every coaching room
  // for the badge), so the unread count and last-message preview on each row
  // cost no request of their own.
  const { clientRoomIndex, unreadByRoom } = useRoomsUnread();
  const { sessionUtils } = useUtils();
  const isSplitSegment = listSegment === SPLIT_VIEW_SEGMENT;
  /** Float clients with chat activity to the top of the loaded rows. */
  const [textedFirst, setTextedFirst] = useState(true);

  // One cache entry per search/sort/segment combination (LRU-capped).
  const queryKey = useMemo(
    () =>
      serializeUserListKey({
        search: searchTerm && searchTerm.trim() ? searchTerm.trim() : "",
        sortBy: sorting?.[0]?.id || "",
        sortOrder: sorting?.[0] ? (sorting[0].desc ? "desc" : "asc") : "",
        segment: listSegment || "",
      }),
    [searchTerm, sorting, listSegment]
  );

  const buildFetchParams = useCallback(() => {
    let sort_by = null;
    let sort_order = "asc";
    if (sorting && sorting.length > 0) {
      const sort = sorting[0];
      sort_by = sort.id;
      sort_order = sort.desc ? "desc" : "asc";
    }
    const search = searchTerm && searchTerm.trim() ? searchTerm.trim() : null;
    return {
      search,
      sort_by,
      sort_order,
      filters: buildListSegmentFilters(listSegment),
      myUsersOnly: listSegment === "my_users",
    };
  }, [searchTerm, sorting, listSegment]);

  // Persist the accumulated pages for the active query combo, bounded so a
  // deep scroll can't grow the cache unbounded.
  const writeQueryCache = useCallback((accumulated, total, hasMoreData, offset) => {
    const trimmed = accumulated.slice(0, USER_LIST_MAX_ROWS_PER_QUERY);
    userListCache.set(queryKeyRef.current, {
      users: trimmed,
      totalCount: total,
      hasMore: trimmed.length < accumulated.length ? true : hasMoreData,
      offset: Math.min(offset, trimmed.length),
    });
  }, []);

  // Fetch users - removed fetchData from dependencies to avoid infinite loops
  const fetchData = useCallback(
    async (reset = false, { silent = false } = {}) => {
      if (loadingRef.current) return;

      if (!silent) setLoading(true);
      loadingRef.current = true;

      try {
        const currentOffset = reset ? 0 : offsetRef.current;
        const { search, sort_by, sort_order, filters, myUsersOnly } = buildFetchParams();

        const res = await fetchAllUserDetails(
          EVENT_TYPE_ID,
          limit,
          currentOffset,
          search,
          sort_by,
          sort_order,
          filters,
          myUsersOnly
        );
        if (res.success && res.data) {
          const newUsers = res.data.users || [];
          const total = res.data.total_count || 0;
          const hasMoreData = res.data.has_more || false;
          const accumulated = reset ? newUsers : [...usersRef.current, ...newUsers];

          setUsers(accumulated);
          usersRef.current = accumulated;
          offsetRef.current = currentOffset + limit;
          setOffset(offsetRef.current);
          setTotalCount(total);
          setHasMore(hasMoreData);
          writeQueryCache(accumulated, total, hasMoreData, offsetRef.current);
        } else {
          setToastMessage(res.message || "Failed to fetch users");
          setToastType("error");
        }
      } catch (err) {
        setToastMessage(err.message || "Error fetching users");
        setToastType("error");
      } finally {
        if (!silent) setLoading(false);
        loadingRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limit, buildFetchParams, writeQueryCache]
  );

  // Silently refetch page 0 behind cached rows; swap them in only if the
  // first page actually changed. Discards its result if the user paged or
  // switched query while it was in flight.
  const revalidateFirstPage = useCallback(
    async (key) => {
      // Guard against overlapping revalidations (StrictMode's double-mount
      // fires this effect twice, and a fast query switch can re-enter).
      if (revalidatingRef.current) return;
      revalidatingRef.current = true;
      const startOffset = offsetRef.current;
      try {
        const { search, sort_by, sort_order, filters, myUsersOnly } = buildFetchParams();
        const res = await fetchAllUserDetails(
          EVENT_TYPE_ID,
          limit,
          0,
          search,
          sort_by,
          sort_order,
          filters,
          myUsersOnly
        );
        if (!res.success || !res.data) return;
        if (
          queryKeyRef.current !== key ||
          offsetRef.current !== startOffset ||
          loadingRef.current
        ) {
          return;
        }

        const freshUsers = res.data.users || [];
        const total = res.data.total_count || 0;
        const hasMoreData = res.data.has_more || false;
        const cachedFirstPage = usersRef.current.slice(0, limit);
        const isUnchanged =
          freshUsers.length === cachedFirstPage.length &&
          freshUsers.every(
            (u, i) =>
              u.user_id === cachedFirstPage[i]?.user_id &&
              u.latest_plan_expiry_date === cachedFirstPage[i]?.latest_plan_expiry_date
          );

        if (isUnchanged) {
          setTotalCount(total);
          writeQueryCache(usersRef.current, total, hasMoreRef.current, offsetRef.current);
          return;
        }
        setUsers(freshUsers);
        usersRef.current = freshUsers;
        setTotalCount(total);
        setHasMore(hasMoreData);
        offsetRef.current = limit;
        setOffset(limit);
        writeQueryCache(freshUsers, total, hasMoreData, limit);
      } catch (err) {
        // Silent revalidation: cached rows stay on screen.
        console.error("User list revalidation failed:", err);
      } finally {
        revalidatingRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limit, buildFetchParams, writeQueryCache]
  );

  const handleRefresh = useCallback(async () => {
    if (loadingRef.current) return;
    // Refresh resets every stale query combo, not just the active one, and
    // drops cached client profiles so the next open refetches.
    userListCache.clear();
    myUserProfileCache.clear();
    setRefreshing(true);
    try {
      offsetRef.current = 0;
      setOffset(0);
      await fetchData(true, { silent: true });
    } finally {
      setRefreshing(false);
    }
  }, [fetchData]);

  // Keep refs up to date
  useEffect(() => {
    fetchDataRef.current = fetchData;
    hasMoreRef.current = hasMore;
  }, [fetchData, hasMore]);

  // Debounce search input - wait for user to finish typing before searching
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(globalFilter);
    }, 800); // 800ms debounce - allows users to finish typing before search triggers

    return () => clearTimeout(timer);
  }, [globalFilter]);

  // Initial fetch and refetch when the query combo changes. A fresh cache
  // hit paints instantly (no loading state) and revalidates silently.
  useEffect(() => {
    queryKeyRef.current = queryKey;
    const cached = userListCache.get(queryKey);
    if (cached) {
      setUsers(cached.users);
      usersRef.current = cached.users;
      setTotalCount(cached.totalCount);
      setHasMore(cached.hasMore);
      offsetRef.current = cached.offset;
      setOffset(cached.offset);
      revalidateFirstPage(queryKey);
    } else {
      offsetRef.current = 0;
      setOffset(0);
      fetchData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  const listSegmentButtons = useMemo(
    () => [
      {
        id: "my_users",
        label: "My users",
        title:
          "Clients assigned to you as diet or fitness coach (session user_id; matches employee or profile where stored).",
      },
      {
        id: "never_subscribed",
        label: "Never subscribed",
        title: "No subscription rows (deleted rows excluded).",
      },
      {
        id: "lapsed",
        label: "Subscribed before, no active plan",
        title: "Had at least one subscription and not currently subscribed (expired or lapsed).",
      },
      {
        id: "inactive_sub_plan_context",
        label: "No active sub · plan vs sub mismatch (history)",
        title:
          "Not currently subscribed, but some diet or workout TargetPlan duration (any past or open-ended window) is not fully covered by any single subscription period. Uses TargetPlanDurations vs subscription effective windows.",
      },
      {
        id: "currently_subscribed",
        label: "Currently subscribed",
        title: "Active plan not expired, or subscription start is in the future (scheduled).",
      },
      {
        id: "missing_payment_date",
        label: "Sub · missing payment date",
        title:
          "At least one subscription is missing a payment date (payment_date is null on that row; non-deleted subscriptions only).",
      },
      {
        id: "expiry_60d",
        label: "Expiry < 60 days",
        title: "Latest plan end date is between now and 60 days from now (not already expired).",
      },
    ],
    []
  );

  const toggleListSegment = useCallback((id) => {
    setListSegment((prev) => (prev === id ? null : id));
  }, []);

  const activeFilterCount = useMemo(() => (listSegment ? 1 : 0), [listSegment]);

  // Infinite scroll handler using Intersection Observer
  useEffect(() => {
    // Wait for loading to complete and DOM to be ready
    if (loading || !hasMore) {
      return;
    }

    let observer = null;
    let scrollTimeoutId = null;
    let cleanupFn = null;

    const timeoutId = setTimeout(() => {
      const sentinel = sentinelRef.current;
      const container = scrollContainerRef.current;
      
      if (!sentinel || !container || !hasMore || loadingRef.current) {
        return;
      }

      // Primary: Intersection Observer
      const isScrollable = container.scrollHeight > container.clientHeight;
      observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          // Check all conditions before fetching - use refs for latest values
          if (entry.isIntersecting && hasMoreRef.current && !loadingRef.current && fetchDataRef.current) {
            fetchDataRef.current(false);
          }
        },
        {
          root: isScrollable ? container : null,
          rootMargin: "200px",
          threshold: 0.1,
        }
      );

      observer.observe(sentinel);

      // Fallback: Scroll event handler
      const handleScroll = () => {
        clearTimeout(scrollTimeoutId);
        scrollTimeoutId = setTimeout(() => {
          if (!container || !hasMoreRef.current || loadingRef.current || !fetchDataRef.current) return;
          const { scrollTop, scrollHeight, clientHeight } = container;
          const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

          if (scrollPercentage > 0.85 && hasMoreRef.current && !loadingRef.current) {
            fetchDataRef.current(false);
          }
        }, 150);
      };

      container.addEventListener("scroll", handleScroll, { passive: true });

      // Store cleanup function
      cleanupFn = () => {
        if (observer) {
          observer.disconnect();
        }
        clearTimeout(scrollTimeoutId);
        container.removeEventListener("scroll", handleScroll);
      };
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [hasMore, loading]);

  const handleClickProfile = useCallback((id) => {
    localStorage.setItem("userId", JSON.stringify(id));
    setUserId(id);
    navigate(`/dashboard/${id}`);
  }, [setUserId, navigate]);

  // Warm the profile on hover/focus so the full dashboard page's ProfileCard
  // paints from cache on click. No-op outside the "My users" segment, where
  // there's no coaching room to prefetch against.
  const handlePrefetchProfile = useCallback((id) => {
    if (!isSplitSegment || !id) return;
    const eventTypeId = resolveProfileEventTypeId(sessionUtils);
    if (eventTypeId) prefetchUserProfile(eventTypeId, id);
  }, [isSplitSegment, sessionUtils]);

  // Format date and time helper
  const formatDateTime = useCallback((timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    const formattedDate = formatLocalDate(date);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${formattedDate} ${hours}:${minutes} ${ampm}`;
  }, []);

  // Generate columns: Name, Joining Date, Phone, Email, # subs, # diet/workout plans, current sub, plan, coaches, expiry, sessions, DOB
  const columns = useMemo(() => {
    const cols = [
      // Name column
      columnHelper.accessor("name", {
        header: "Name",
        size: 150,
        minSize: 100,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: false, // Name is composite, can't sort directly
        cell: (info) => {
          const row = info.row.original;
          const firstName = row.user_first_name || row.first_name || "";
          const lastName = row.user_last_name || row.last_name || "";
          const fullName = [firstName, lastName].filter(Boolean).join(" ");
          // Chat context only exists for the coach's own clients, and only the
          // my_users segment lists them — elsewhere the lookup would always
          // miss and the extra row height would buy nothing.
          const room = isSplitSegment
            ? selectClientRoom(clientRoomIndex, unreadByRoom, row.user_id)
            : null;

          return (
            <div className="min-w-0 text-left">
              <div className="flex items-center gap-2">
                <span
                  className={`truncate text-sm text-gray-900 ${
                    room?.unread ? "font-semibold" : "font-medium"
                  }`}
                  title={fullName || "-"}
                >
                  {fullName || "-"}
                </span>
                {room?.unread > 0 && (
                  <span
                    aria-label={`${room.unread} unread messages`}
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-orange-primary px-1.5 text-[10px] font-bold leading-4 text-white"
                  >
                    <MessageSquare className="h-2.5 w-2.5" />
                    {room.unread > 99 ? "99+" : room.unread}
                  </span>
                )}
              </div>
              {room?.lastMessage && (
                <div
                  className="truncate text-xs text-gray-500"
                  title={room.lastMessage}
                >
                  {room.lastMessage}
                </div>
              )}
            </div>
          );
        },
      }),
      // Joining Date column (second column with date and time)
      columnHelper.accessor("user_joining_date", {
        header: "Joining Date",
        size: 180,
        minSize: 120,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm text-gray-700 text-center">
              {value ? formatDateTime(value) : "-"}
            </div>
          );
        },
      }),
      // Phone column
      columnHelper.accessor("user_phone_number", {
        header: "Phone",
        size: 150,
        minSize: 100,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm text-gray-700 text-center">
              {value !== undefined && value !== null ? String(value) : "-"}
            </div>
          );
        },
      }),
      // Email column
      columnHelper.accessor("user_email", {
        header: "Email",
        size: 200,
        minSize: 150,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm text-gray-700 text-center truncate" title={value || "-"}>
              {value !== undefined && value !== null ? String(value) : "-"}
            </div>
          );
        },
      }),
      columnHelper.accessor("subscription_count", {
        header: "# Subscriptions",
        size: 130,
        minSize: 100,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          const n = value !== undefined && value !== null ? Number(value) : 0;
          return (
            <div className="text-sm text-gray-700 text-center">
              {Number.isFinite(n) ? n : 0}
            </div>
          );
        },
      }),
      columnHelper.accessor("diet_plan_count", {
        header: "# Diet Plans",
        size: 120,
        minSize: 90,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          const n = value !== undefined && value !== null ? Number(value) : 0;
          return (
            <div className="text-sm text-gray-700 text-center">
              {Number.isFinite(n) ? n : 0}
            </div>
          );
        },
      }),
      columnHelper.accessor("workout_plan_count", {
        header: "# Workout Plans",
        size: 130,
        minSize: 100,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          const n = value !== undefined && value !== null ? Number(value) : 0;
          return (
            <div className="text-sm text-gray-700 text-center">
              {Number.isFinite(n) ? n : 0}
            </div>
          );
        },
      }),
      columnHelper.accessor("is_currently_subscribed", {
        header: "Currently Subscribed",
        size: 150,
        minSize: 120,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          const yes = value === true || value === "true" || value === 1;
          const no = value === false || value === "false" || value === 0;
          const label = yes ? "Yes" : no ? "No" : "-";
          return (
            <div className="text-sm text-gray-700 text-center">{label}</div>
          );
        },
      }),
      columnHelper.accessor("plan_name", {
        header: "Plan Name",
        size: 160,
        minSize: 120,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm text-gray-700 text-center truncate" title={value || "-"}>
              {value !== undefined && value !== null && String(value).trim() !== "" ? String(value) : "-"}
            </div>
          );
        },
      }),
      columnHelper.accessor("diet_coach_name", {
        header: "Diet Coach",
        size: 150,
        minSize: 110,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm text-gray-700 text-center truncate" title={value || "-"}>
              {value !== undefined && value !== null && String(value).trim() !== "" ? String(value) : "-"}
            </div>
          );
        },
      }),
      columnHelper.accessor("fitness_coach_name", {
        header: "Fitness Coach",
        size: 150,
        minSize: 110,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm text-gray-700 text-center truncate" title={value || "-"}>
              {value !== undefined && value !== null && String(value).trim() !== "" ? String(value) : "-"}
            </div>
          );
        },
      }),
      columnHelper.accessor("latest_plan_expiry_date", {
        header: "Latest Plan Expiry",
        size: 200,
        minSize: 140,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          if (!value) return <div className="text-sm text-gray-700 text-center">-</div>;
          const status = expiryStatus(value);
          return (
            <div className="text-center">
              <div
                className={`text-sm ${
                  status && status.tone !== "ok"
                    ? `font-semibold ${EXPIRY_TEXT[status.tone]}`
                    : "text-gray-700"
                }`}
              >
                {formatDateTime(value)}
              </div>
              {status && status.tone !== "ok" && (
                <span
                  className={`mt-0.5 inline-block rounded px-1.5 py-px text-[10px] font-semibold ${
                    EXPIRY_CHIP[status.tone]
                  }`}
                >
                  {status.label}
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor("nearest_subscription_start_date", {
        header: "Nearest Sub Start",
        size: 200,
        minSize: 140,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm text-gray-700 text-center">
              {value ? formatDateTime(value) : "-"}
            </div>
          );
        },
      }),
      // Session Count column
      columnHelper.accessor("session_count", {
        header: "Session Count",
        size: 140,
        minSize: 120,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm text-gray-700 text-center">
              {value !== undefined && value !== null ? value : 0}
            </div>
          );
        },
      }),
      // Date of Birth column
      columnHelper.accessor("user_date_of_birth", {
        header: "Date of Birth",
        size: 150,
        minSize: 120,
        maxSize: 1000,
        enableResizing: true,
        enableSorting: true,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm text-gray-700 text-center">
              {value ? formatLocalDate(new Date(value)) : "-"}
            </div>
          );
        },
      }),
    ];

    return cols;
  }, [formatDateTime, isSplitSegment, clientRoomIndex, unreadByRoom]);

  /**
   * Rows with chat activity floated to the top, within whatever order the
   * server returned. Purely a re-order of the pages already loaded — the API
   * has no chat-activity sort key, so a client who texted but sits on an
   * unloaded page only rises once that page arrives.
   *
   * Only in the "My users" segment: `clientRoomIndex` covers rooms the viewer
   * can see, which is also the only segment that shows chat on a row.
   */
  const orderedUsers = useMemo(() => {
    if (!isSplitSegment || !textedFirst) return users;
    return users
      .map((user, index) => ({
        user,
        index,
        room: selectClientRoom(clientRoomIndex, unreadByRoom, user.user_id),
      }))
      .sort(
        (a, b) =>
          // Texted you and still unanswered — the unambiguous signal.
          (b.room?.unread || 0) - (a.room?.unread || 0) ||
          // Then any activity at all. `last_seq` is per-room, so it's a rough
          // recency proxy across rooms — the same trade-off InboxList's
          // byActivity already makes.
          (b.room?.lastSeq || 0) - (a.room?.lastSeq || 0) ||
          // Otherwise leave the server's order alone.
          a.index - b.index
      )
      .map((entry) => entry.user);
  }, [users, isSplitSegment, textedFirst, clientRoomIndex, unreadByRoom]);

  // Initialize table
  const table = useReactTable({
    data: orderedUsers,
    columns,
    state: {
      sorting,
      columnSizing,
    },
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    // Sorting is a server concern here — `sort_by`/`sort_order` go out with
    // every fetch. A client-side sorted row model would both duplicate that
    // work and undo the texted-first pass above.
    manualSorting: true,
  });

  return (
    <div className="flex-1 flex flex-col items-center relative">
      {loading && users.length === 0 ? (
        <Loader />
      ) : (
        <div
          className="container px-4 py-4 mx-auto w-full relative overflow-auto"
          ref={scrollContainerRef}
        >
          {/* Loading Overlay - shows during filter/sort/search operations */}
          {loading && users.length > 0 && (
            <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50 backdrop-blur-sm pointer-events-auto">
              <div className="flex flex-col items-center gap-3">
                <Loader />
                <p className="text-sm font-medium text-gray-700">Loading...</p>
              </div>
            </div>
          )}
          {/* Header with count, filters, and search */}
          <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <p className="text-sm font-medium text-gray-700">
                Total Users: <span className="text-orange-primary font-semibold">{totalCount}</span>
              </p>
              <RefreshButton
                onRefresh={handleRefresh}
                refreshing={refreshing}
                title="Refresh users"
              />
              {activeFilterCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">
                    {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                  </span>
                  <button
                    onClick={() => setListSegment(null)}
                    className="text-xs text-red-600 hover:text-red-800 font-medium underline"
                    title="Clear all filters"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Search users..."
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-primary focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="mb-4">
            <div className="flex flex-wrap items-center gap-2">
              {listSegmentButtons.map(({ id, label, title }) => {
                const isSelected = listSegment === id;
                return (
                  <button
                    key={id}
                    type="button"
                    title={title}
                    onClick={() => toggleListSegment(id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      isSelected
                        ? "bg-orange-primary text-white border-orange-primary shadow-sm"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-[#FFF3EB] hover:border-orange-primary/50"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}

              {/* Only meaningful where rows carry chat: the reorder reads the
                  same room index the Name column does. */}
              {isSplitSegment && (
                <button
                  type="button"
                  aria-pressed={textedFirst}
                  title="Float clients with unread messages and recent chat activity to the top of the rows already loaded."
                  onClick={() => setTextedFirst((v) => !v)}
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                    textedFirst
                      ? "border-orange-primary bg-orange-primary text-white shadow-sm"
                      : "border-gray-300 bg-white text-gray-700 hover:border-orange-primary/50 hover:bg-[#FFF3EB]"
                  }`}
                >
                  <MessageSquare className="h-4 w-4" />
                  Texted first
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table style={{ width: '100%', tableLayout: 'auto' }}>
                <thead className="bg-light-orange sticky top-0 z-10">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, index) => (
                        <th
                          key={header.id}
                          style={{ minWidth: header.column.columnDef.minSize || 100, width: 'auto' }}
                          className={`px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap relative ${
                            index < headerGroup.headers.length - 1 ? 'border-r border-gray-200' : ''
                          }`}
                        >
                          <div className="flex items-center justify-center gap-1">
                            {header.column.getCanSort() ? (
                              <button
                                onClick={header.column.getToggleSortingHandler()}
                                className="flex items-center justify-center gap-2 hover:bg-[#FFE5D0] -mx-2 px-2 py-1 rounded transition-colors cursor-pointer"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{
                                  asc: <ArrowUp className="w-4 h-4 text-orange-primary" />,
                                  desc: <ArrowDown className="w-4 h-4 text-orange-primary" />,
                                }[header.column.getIsSorted()] ?? (
                                  <ArrowUpDown className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                            ) : (
                              flexRender(header.column.columnDef.header, header.getContext())
                            )}
                          </div>
                          {header.column.getCanResize() && (
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-orange-primary bg-gray-300"
                              style={{ userSelect: 'none' }}
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                        {globalFilter
                          ? "No users found matching your search."
                          : listSegment
                            ? "No users match this filter."
                            : "No users available."}
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        role="button"
                        tabIndex={0}
                        data-testid={`user-row-${row.original.user_id}`}
                        aria-label={`Open profile for ${row.original.name || "user"}`}
                        onClick={() => handleClickProfile(row.original.user_id)}
                        onMouseEnter={() =>
                          handlePrefetchProfile(row.original.user_id)
                        }
                        onFocus={() =>
                          handlePrefetchProfile(row.original.user_id)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleClickProfile(row.original.user_id);
                          }
                        }}
                        className="hover:bg-[#FFF3EB] transition-colors cursor-pointer"
                      >
                        {row.getVisibleCells().map((cell, index) => {
                          const isNameColumn = cell.column.id === 'name';
                          return (
                            <td 
                              key={cell.id}
                              style={{ minWidth: cell.column.columnDef.minSize || 100, width: 'auto' }}
                              className={`px-6 py-4 whitespace-nowrap ${
                                index < row.getVisibleCells().length - 1 ? 'border-r border-gray-200' : ''
                              } ${isNameColumn ? 'text-left' : 'text-center'}`}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {/* Sentinel element for infinite scroll detection */}
              <div ref={sentinelRef} style={{ height: "20px" }} />
              {loading && users.length > 0 && (
                <div className="text-center py-8 bg-gray-50 flex flex-col items-center justify-center gap-3">
                  <Loader />
                  <p className="text-gray-600 text-sm font-medium">Loading more users...</p>
                </div>
              )}
              {!hasMore && users.length > 0 && (
                <div className="text-center py-4 bg-gray-50">
                  <p className="text-gray-500 text-sm">No more users to load</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <Toaster message={toastMessage} type={toastType} />
      )}
    </div>
  );
};

export default UserListDashboard;
